import type { WavesChart } from '@shared/wavesChart';
import type { WavesLayout } from '../wavesPlayfieldGeometry';
import { corridorCanvasBounds } from '../wavesPlayfieldGeometry';
import type { RenderQuality } from '../../rendering/renderQuality';
import {
  BEAT_PULSE_STRENGTH,
  STAR_COUNT,
  STAR_DRIFT_FRAC_PER_BEAT,
} from './wavesRenderConstants';
import { hexToRgba } from './wavesRenderColour';

interface Star {
  /** Normalised X in [0, 2) — multiplied by canvas width for wrap drift. */
  normX: number;
  /** Normalised Y in [0, 1]. */
  normY: number;
  size: number;
  alpha: number;
}

/**
 * Owns cached background gradients and a pre-built star field. Both
 * are render-only state — the scene pass never touches engine data.
 */
export class WavesSceneRenderer {
  private bgGradient: CanvasGradient | null = null;
  private bgGradientKey = '';
  private readonly stars: Star[] = [];
  private readonly quality: RenderQuality;

  constructor(quality: RenderQuality) {
    this.quality = quality;
    const count = quality.decorations ? STAR_COUNT : 0;
    this.initStars(count);
  }

  /** Drop cached gradients on resize so the next draw rebuilds them. */
  invalidate(): void {
    this.bgGradient = null;
    this.bgGradientKey = '';
  }

  draw(
    ctx: CanvasRenderingContext2D,
    layout: WavesLayout,
    chart: WavesChart,
    beat: number,
    canvasWidth: number,
    canvasHeight: number,
    beatPulse: number,
  ): void {
    const palette = chart.palette;

    const key = `${canvasHeight}|${palette.bg}|${palette.bgAccent}`;
    if (!this.bgGradient || this.bgGradientKey !== key) {
      const grad = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      grad.addColorStop(0, palette.bg);
      grad.addColorStop(0.6, palette.bgAccent);
      grad.addColorStop(1, palette.bg);
      this.bgGradient = grad;
      this.bgGradientKey = key;
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (this.quality.backgroundBloom && beatPulse > 0.05) {
      const cx = layout.offsetX + layout.widthPx * 0.5;
      const cy = layout.offsetY + layout.heightPx * 0.5;
      const radial = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        Math.max(canvasWidth, canvasHeight) * 0.55,
      );
      radial.addColorStop(0, hexToRgba(palette.ui, 0.08 * beatPulse * BEAT_PULSE_STRENGTH));
      radial.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = radial;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    if (this.stars.length > 0) {
      this.drawStars(ctx, beat, canvasWidth, canvasHeight, palette.ui, beatPulse);
    }

    this.drawCorridorBackdrop(ctx, layout, canvasWidth, palette.bgAccent, beatPulse);
  }

  private initStars(count: number): void {
    this.stars.length = 0;
    for (let i = 0; i < count; i += 1) {
      const seed = (i * 9301 + 49297) % 233280;
      const ySeed = (i * 4096 + 7841) % 233280;
      const sizeSeed = (i * 13 + 31) % 100;
      this.stars.push({
        normX: seed / 233280 * 2,
        normY: ySeed / 233280,
        size: 0.6 + (sizeSeed / 100) * 1.8,
        alpha: 0.18 + (sizeSeed / 100) * 0.45,
      });
    }
  }

  private drawStars(
    ctx: CanvasRenderingContext2D,
    beat: number,
    canvasWidth: number,
    canvasHeight: number,
    starColour: string,
    beatPulse: number,
  ): void {
    ctx.save();
    ctx.fillStyle = starColour;
    // Stars live in a virtual `canvasWidth * 1.4` strip (offset −0.2 ×
    // canvasWidth to padding-left), so the drift wraps inside the SAME
    // strip — otherwise drift wrapping at `canvasWidth` would snap the
    // whole field 0.4 × canvasWidth leftward every wrap, visibly stuttering.
    const wrapWidth = canvasWidth * 1.4;
    const drift = (beat * canvasWidth * STAR_DRIFT_FRAC_PER_BEAT) % wrapWidth;
    const pulseBoost = 1 + beatPulse * BEAT_PULSE_STRENGTH * 0.4;
    const pulseAlpha = 0.85 + beatPulse * 0.15;
    for (const star of this.stars) {
      const baseX = star.normX * canvasWidth;
      const x = ((baseX - drift) % wrapWidth + wrapWidth) % wrapWidth - canvasWidth * 0.2;
      const y = star.normY * canvasHeight;
      ctx.globalAlpha = star.alpha * pulseAlpha;
      ctx.beginPath();
      ctx.arc(x, y, star.size * pulseBoost, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawCorridorBackdrop(
    ctx: CanvasRenderingContext2D,
    layout: WavesLayout,
    canvasWidth: number,
    accent: string,
    beatPulse: number,
  ): void {
    const { topY, bottomY } = corridorCanvasBounds(layout);
    const corridorHeight = bottomY - topY;
    const padding = corridorHeight * 0.08;
    ctx.save();
    ctx.globalAlpha = 0.12 + beatPulse * BEAT_PULSE_STRENGTH * 0.08;
    ctx.fillStyle = accent;
    // Full canvas width so the accent band sits behind the corridor
    // edge to edge, matching the terrain fills' full-bleed extent.
    ctx.fillRect(0, topY - padding, canvasWidth, corridorHeight + padding * 2);
    ctx.restore();
  }
}
